import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
} from "reactstrap";
import Papa from "papaparse";
import SelectInput from "components/common/SelectInput";
import axios from "axiosClient";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import moment from "moment";

const HEADER_ALIASES = {
  title: ["Title", "title"],
  firstName: ["First Name", "First name", "Firstname", "first_name"],
  lastName: ["Last Name", "Last name", "Lastname", "last_name"],
  idDoc: ["ID Doc #", "ID Doc", "ID", "Passport", "Passport No"],
  paymentDate: ["Payment Date", "Payment date"],
  receiptNo: ["Receipt No", "Receipt", "Receipt Number"],
  venueName: ["Venue Name", "Venue"],
  testCentreCity: ["Test Centre City", "City"],
  testDate: ["Test Date / Time (LRW)", "Test Date", "Exam Date", "LRW Date"],
  productName: ["Product Name", "Product", "Exam"],
  osrComponent: ["OSR Component", "OSR"],
  delivery: ["Delivery"],
};

const DISPLAY_HEADERS = [
  "Title",
  "First Name",
  "Last Name",
  "ID Doc #",
  "Payment Date",
  "Receipt No",
  "Venue Name",
  "Test Centre City",
  "Test Date / Time (LRW)",
  "Product Name",
  "OSR Component",
  "Delivery",
  "PINFL",
  "AMOUNT ",
  "PAYMENT METHOD",
];

const DISPLAY_HEADERS_TKT = [
  "First Name",
  "Last Name",
  "ID Doc #",
  "Receipt No",
  "Test Date",
  "Product Name",
  "PINFL",
  "AMOUNT ",
  "PAYMENT METHOD",
  "Processed By",
];

const COLUMN_WIDTHS = [
  { wch: 10 },
  { wch: 20 },
  { wch: 22 },
  { wch: 16 },
  { wch: 18 },
  { wch: 18 },
  { wch: 30 },
  { wch: 18 },
  { wch: 24 },
  { wch: 28 },
  { wch: 18 },
  { wch: 18 },
  { wch: 22 },
  { wch: 14 },
  { wch: 18 },
];

const normaliseHeader = (header) =>
  header
    ? header
        .replace(/^"+|"+$/g, "")
        .replace(/""/g, '"')
        .trim()
    : "";

const cleanValue = (value) => {
  if (value === undefined || value === null) return "";
  let stringValue = `${value}`.trim();
  if (stringValue.startsWith('"') && stringValue.endsWith('"')) {
    stringValue = stringValue.slice(1, -1);
  }
  return stringValue.replace(/""/g, '"').trim();
};

const normaliseAmount = (value) => {
  if (value === undefined || value === null || value === "") {
    return "";
  }
  const stringValue = `${value}`.trim();
  const numeric = Number(stringValue.replace(/[^0-9.-]/g, ""));
  if (Number.isNaN(numeric)) {
    return stringValue;
  }
  return numeric;
};

const PAPA_BASE_OPTIONS = {
  header: true,
  skipEmptyLines: true,
  transformHeader: (header) => normaliseHeader(header),
};

const runPapaParse = (input) =>
  new Promise((resolve, reject) => {
    Papa.parse(input, {
      ...PAPA_BASE_OPTIONS,
      complete: (results) => resolve(results),
      error: (error) => reject(error),
    });
  });

const needsFallbackParsing = (results) => {
  const fields = results?.meta?.fields || [];
  if (!fields.length) return true;
  if (fields.length === 1 && fields[0] && fields[0].includes(",")) {
    return true;
  }
  return false;
};

const normaliseCsvContent = (content) => {
  const lines = content.split(/\r?\n/);
  if (!lines.length) return content;
  const [firstLine, ...rest] = lines;
  const cleanedHeader = firstLine
    .trim()
    .replace(/^"+|"+$/g, "")
    .replace(/""/g, '"');
  return [cleanedHeader, ...rest].join("\n");
};

const extractValue = (row, key) => {
  const candidates = HEADER_ALIASES[key] || [];
  for (const candidate of candidates) {
    if (row[candidate] !== undefined) {
      return cleanValue(row[candidate]);
    }
    const lowerCandidate = candidate.toLowerCase();
    if (row[lowerCandidate] !== undefined) {
      return cleanValue(row[lowerCandidate]);
    }
  }
  return "";
};

const buildCandidateRows = (data) =>
  (data || [])
    .map((row, index) => {
      const title = extractValue(row, "title");
      const firstName = extractValue(row, "firstName");
      const lastName = extractValue(row, "lastName");
      const idDoc = extractValue(row, "idDoc");
      const paymentDate = extractValue(row, "paymentDate");
      const receiptNo = extractValue(row, "receiptNo");
      const venueName = extractValue(row, "venueName");
      const testCentreCity = extractValue(row, "testCentreCity");
      const testDate = extractValue(row, "testDate");
      const productName = extractValue(row, "productName");
      const osrComponent = extractValue(row, "osrComponent");
      const delivery = extractValue(row, "delivery");

      const hasCoreData =
        idDoc || receiptNo || firstName || lastName || productName;

      if (!hasCoreData) {
        return null;
      }

      return {
        index,
        title,
        firstName,
        lastName,
        idDoc,
        paymentDate,
        receiptNo,
        venueName,
        testCentreCity,
        testDate,
        productName,
        osrComponent,
        delivery,
      };
    })
    .filter(Boolean);

const buildTktCandidateRows = (data) =>
  (data || [])
    .map((row, index) => {
      const firstName = row["firstName"] || "";
      const lastName = row["lastName"] || "";
      const idDoc = row["passportId"] || "";
      const receiptNo = row["enquiryId"] || "";
      const testDate = row["examDate"] || "";
      const productName = row["exam"] || "";

      const hasCoreData = idDoc || receiptNo || firstName || lastName;

      if (!hasCoreData) {
        return null;
      }

      return {
        index,
        firstName,
        lastName,
        idDoc,
        receiptNo,
        testDate,
        productName,
      };
    })
    .filter(Boolean);

const buildWorkbook = (rows, type) => {
  const sheetRows =
    type === "TKT"
      ? [
          DISPLAY_HEADERS_TKT,
          ...rows.map((row) => [
            row.firstName,
            row.lastName,
            row.idDoc,
            row.receiptNo,
            row.testDate,
            row.productName,
            row.lookup?.paymentReceiptNumber || "",
            normaliseAmount(row.lookup?.amount),
            row.lookup?.paymentMethod || "",
            row.lookup?.processedBy || "",
          ]),
        ]
      : [
          DISPLAY_HEADERS,
          ...rows.map((row) => [
            row.title,
            row.firstName,
            row.lastName,
            row.idDoc,
            row.paymentDate,
            row.receiptNo,
            row.venueName,
            row.testCentreCity,
            row.testDate,
            row.productName,
            row.osrComponent,
            row.delivery,
            row.lookup?.paymentReceiptNumber || "",
            normaliseAmount(row.lookup?.amount),
            row.lookup?.paymentMethod || "",
          ]),
        ];

  const worksheet = XLSX.utils.aoa_to_sheet(sheetRows);
  worksheet["!cols"] = COLUMN_WIDTHS;

  sheetRows.forEach((row, rowIndex) => {
    if (rowIndex === 0) return;
    const amountCellRef = XLSX.utils.encode_cell({ c: 13, r: rowIndex });
    const paymentDateCellRef = XLSX.utils.encode_cell({ c: 4, r: rowIndex });
    const testDateCellRef = XLSX.utils.encode_cell({ c: 8, r: rowIndex });

    const amountCell = worksheet[amountCellRef];
    if (amountCell && amountCell.v !== "") {
      amountCell.t = "n";
      worksheet[amountCellRef] = amountCell;
    }

    const dateCell = worksheet[paymentDateCellRef];
    if (dateCell) {
      dateCell.t = "s";
      worksheet[paymentDateCellRef] = dateCell;
    }

    const testDateCell = worksheet[testDateCellRef];
    if (testDateCell) {
      testDateCell.t = "s";
      worksheet[testDateCellRef] = testDateCell;
    }
  });

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Payments");
  const fileName =
    type === "TKT"
      ? `TKT_${moment().format("YYYYMMDD_HHmmss")}.xlsx`
      : `PaymentReceipts_${moment().format("YYYYMMDD_HHmmss")}.xlsx`;
  XLSX.writeFile(workbook, fileName);
};

const ReceiptUploadModal = ({ isOpen, toggle }) => {
  const [type, setType] = useState("IELTS");
  const [fileName, setFileName] = useState("");
  const [parsing, setParsing] = useState(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    if (!isOpen) {
      setType("IELTS");
      setFileName("");
      setParsing(false);
      setRows([]);
      setLoading(false);
      setSummary(null);
    }
  }, [isOpen]);

  const unmatchedRows = useMemo(() => {
    if (!summary || !summary.unmatchedIndexes) return [];
    const unmatchedSet = new Set(summary.unmatchedIndexes);
    return rows.filter((row) => unmatchedSet.has(row.index));
  }, [rows, summary]);

  const handleFileChange = async (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) {
      setRows([]);
      setFileName("");
      return;
    }
    setParsing(true);
    setRows([]);
    setFileName(file.name);
    setSummary(null);

    try {
      const text = await file.text();
      let results = await runPapaParse(text);

      if (needsFallbackParsing(results)) {
        const normalised = normaliseCsvContent(text);
        results = await runPapaParse(normalised);
      }

      if (needsFallbackParsing(results)) {
        throw new Error("Unable to detect CSV headers");
      }
      const parsedRows =
        type === "TKT"
          ? buildTktCandidateRows(results.data)
          : buildCandidateRows(results.data);

      if (!parsedRows.length) {
        toast.warn("No candidate rows detected in the uploaded file.");
      }

      setRows(parsedRows);
    } catch (error) {
      console.error(error);
      toast.error("Failed to parse the CSV file. Please check the format.");
    } finally {
      setParsing(false);
    }
  };

  const handleGenerate = async () => {
    if (!rows.length) {
      toast.warn("Please upload a CSV file with candidate data first.");
      return;
    }
    setLoading(true);
    setSummary(null);

    try {
      const payload = {
        type,
        rows: rows.map((row) => ({
          index: row.index,
          idDoc: row.idDoc,
          receiptNo: row.receiptNo,
          paymentDate: row.paymentDate,
          firstName: row.firstName,
          lastName: row.lastName,
          productName: row.productName,
          venueName: row.venueName,
          testCentreCity: row.testCentreCity,
          testDate: row.testDate,
        })),
      };

      const response = await axios.post(
        "/api/payments-receipts-lookup",
        payload
      );

      const { results = [], summary: lookupSummary } = response.data || {};
      const resultByIndex = new Map();
      results.forEach((item) => {
        resultByIndex.set(item.index, item);
      });

      const hydratedRows = rows.map((row) => ({
        ...row,
        lookup: resultByIndex.get(row.index) || null,
      }));

      const matchedCount = results.filter((item) => item?.matched).length;
      const unmatchedSet = new Set(
        results.filter((item) => !item?.matched).map((item) => item.index)
      );

      const seenIndexes = new Set(results.map((item) => item.index));
      rows.forEach((row) => {
        if (!seenIndexes.has(row.index)) {
          unmatchedSet.add(row.index);
        }
      });

      const summaryMatched = lookupSummary?.matched ?? matchedCount;
      const summaryTotal = lookupSummary?.total ?? rows.length;
      const summaryUnmatched =
        lookupSummary?.unmatched ?? summaryTotal - summaryMatched;
      const normalizedUnmatched = summaryUnmatched < 0 ? 0 : summaryUnmatched;

      setSummary({
        total: rows.length,
        matched: summaryMatched,
        unmatched: normalizedUnmatched,
        unmatchedIndexes: Array.from(unmatchedSet).sort((a, b) => a - b),
      });

      buildWorkbook(hydratedRows, type);

      if (unmatchedSet.size) {
        toast.warn(
          `${unmatchedSet.size} records were not matched with existing payments.`
        );
      } else {
        toast.success("Payment receipt file generated successfully.");
      }
    } catch (error) {
      console.error(error);
      toast.error("Unable to fetch payment details from the server.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg">
      <ModalHeader toggle={toggle}>Generate Payment Receipt XLSX</ModalHeader>
      <ModalBody>
        <FormGroup>
          <Label for="typeSwitch">Type</Label>
          <SelectInput
            inputId="typeSwitch"
            value={type}
            isDisabled={loading}
            onChange={setType}
            options={["IELTS", "TKT"]}
          />
        </FormGroup>
        <FormGroup>
          <Label for="paymentReceiptsCsv">
            Upload {type} CSV file {type === "TKT" ? "(enquiries.csv)" : ""}
          </Label>
          <Input
            id="paymentReceiptsCsv"
            type="file"
            accept=".csv,text/csv"
            disabled={loading}
            onChange={handleFileChange}
          />
        </FormGroup>
        {fileName && (
          <p className="text-muted">
            Selected file: <strong>{fileName}</strong>
          </p>
        )}
        {parsing && (
          <div className="d-flex align-items-center">
            <Spinner size="sm" className="mr-2" />
            <span>Parsing candidate data…</span>
          </div>
        )}
        {rows.length > 0 && !parsing && (
          <div className="mt-3">
            <p className="mb-2">
              Loaded <strong>{rows.length}</strong> candidate
              {rows.length === 1 ? "" : "s"} from the file.
            </p>
            {summary && (
              <div className="mb-2">
                <p className="mb-1">
                  Matched: <strong>{summary.matched}</strong>
                </p>
                <p className="mb-1">
                  Unmatched: <strong>{summary.unmatched}</strong>
                </p>
              </div>
            )}
            {unmatchedRows.length > 0 && (
              <div className="p-2">
                <p className="mb-1 font-weight-bold">
                  Unmatched candidates ({unmatchedRows.length}):
                </p>
                <div
                  className="bg-white"
                  style={{ maxHeight: "200px", overflowY: "auto" }}
                >
                  <ul className="mb-0 pl-3 py-2">
                    {unmatchedRows.map((row) => (
                      <li key={`unmatched-${row.index}`}>
                        {row.firstName || row.lastName
                          ? `${row.firstName} ${row.lastName}`.trim()
                          : row.idDoc ||
                            row.receiptNo ||
                            `Row ${row.index + 1}`}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={toggle} disabled={loading}>
          Close
        </Button>
        <Button
          color="primary"
          onClick={handleGenerate}
          disabled={!rows.length || parsing || loading}
        >
          {loading ? <Spinner size="sm" /> : "Generate XLSX"}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default ReceiptUploadModal;
